import { DehydratedState } from '@tanstack/react-query';
import { NextSeo, NextSeoProps } from 'next-seo';
import { DEFAULT_SEO_CONFIG, ROOT_URL } from 'next-seo.config';
import { OpenGraph } from 'next-seo/lib/types';
import { useRouter } from 'next/router';
import { useMemo } from 'react';

import { Footer } from '@/components/Footer';
import { Header } from '@/components/Header';

import { useTokenResult } from '@/hooks/tokenQueries/useTokenResult';

import type { CachedWalletAddresses } from '@/utils/cachedWalletAddresses';
import { ChainAddress } from '@/utils/models';
import { routes } from '@/utils/routes';

import { TradePageContent } from './TradePageContent';

const TRADE_PAGE_TITLE = 'Swap tokens and exchange crypto with no fees | Matcha DEX aggregator';
const TRADE_PAGE_DESCRIPTION =
  'Swap tokens with zero fees across 9 crypto networks. Instantly trade over 6 million tokens with deep aggregated liquidity from 130+ exchanges!';

export type TradePageServerProps = {
  cachedWalletAddresses?: CachedWalletAddresses | null;
  dehydratedState?: DehydratedState | null;
};

export type TradePageProps = TradePageServerProps & {
  iFrameContext: boolean;
};

const normalizeQueryParam = (param: string | string[] | undefined) =>
  Array.isArray(param) ? param[0] : param;

export const TradePage = (props: TradePageProps) => {
  const router = useRouter();
  const { sellAddress, sellChain, buyAddress, buyChain } = router.query;
  const { iFrameContext } = props;

  const sellAddressParam = normalizeQueryParam(sellAddress);
  const sellChainParam = normalizeQueryParam(sellChain);
  const buyAddressParam = normalizeQueryParam(buyAddress);
  const buyChainParam = normalizeQueryParam(buyChain);

  const { token: sellToken } = useTokenResult(
    sellAddressParam as ChainAddress | undefined,
    sellChainParam ? Number(sellChainParam) : undefined,
  );
  const { token: buyToken } = useTokenResult(
    buyAddressParam as ChainAddress | undefined,
    buyChainParam ? Number(buyChainParam) : undefined,
  );

  const seo: NextSeoProps & OpenGraph = useMemo(() => {
    const queryParams: Record<string, string> = {};

    if (sellToken) {
      queryParams.sellAddress = sellToken.address;
      queryParams.sellChain = sellToken.chainId.toString();
    }

    if (buyToken) {
      queryParams.buyAddress = buyToken.address;
      queryParams.buyChain = buyToken.chainId.toString();
    }

    const canonicalUrl = `${ROOT_URL}${routes.TRADE}`;
    const queryString = new URLSearchParams(queryParams).toString();
    const url = `${canonicalUrl}${queryString ? `?${queryString}` : ''}`;
    const ogImage = `${ROOT_URL}/images/og/social-banner.jpg`;

    return {
      ...DEFAULT_SEO_CONFIG,
      title: TRADE_PAGE_TITLE,
      description: TRADE_PAGE_DESCRIPTION,
      canonical: canonicalUrl,
      openGraph: {
        title: TRADE_PAGE_TITLE,
        description: TRADE_PAGE_DESCRIPTION,
        images: [{ url: ogImage, width: 1200, height: 630, alt: 'Matcha' }],
      },
      additionalMetaTags: [
        { name: 'twitter:title', content: TRADE_PAGE_TITLE },
        { name: 'twitter:description', content: TRADE_PAGE_DESCRIPTION },
        { name: 'twitter:image', content: ogImage },
        { name: 'twitter:url', content: url },
      ],
    };
  }, [sellToken, buyToken]);

  return (
    <div className="flex flex-col min-h-screen gap-20">
      <NextSeo {...seo} />
      <Header hideSearch={iFrameContext} />
      <TradePageContent
        className={iFrameContext ? undefined : 'px-2'}
        iFrameContext={iFrameContext}
      />
      {!iFrameContext && <Footer isHomepage={false} />}
    </div>
  );
};
